<template>
    <Breadcrumbs main="Delivery" mains="Picking List" title="Shipping" />
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <h5>Picking Orders by Courier</h5>
                <button class="btn btn-primary btn-sm" @click="showAddOrderModal">
                    <i class="fa fa-plus"></i> Add Order
                </button>
            </div>
            <div class="card-body">
                <div v-if="loading" class="text-center">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else>
                    <!-- Filter Section -->
                    <div class="d-flex flex-wrap mb-2">
                        <div class="col-sm-3 px-1 mb-2">
                            <label class="form-label col-form-label">Shipping Date</label>
                            <input type="date" v-model="shippingDateFilter" class="form-control" placeholder="Select Shipping Date">
                        </div>
                        <div class="col-sm-3 px-1 mb-2">
                            <label class="form-label col-form-label">Order No</label>
                            <input type="text" v-model="orderNumberFilter" class="form-control" placeholder="Enter Order Number">
                        </div>
                        <div class="col-sm-3 px-1 mb-2">
                            <label class="form-label col-form-label">Customer</label>
                            <input type="text" v-model="customerFilter" class="form-control" placeholder="Enter Customer Name">
                        </div>
                    </div>
                    <div v-for="courierGroup in paginatedGroupedOrders" :key="courierGroup.courierId" class="mb-4">
                        <h6>{{ courierGroup.courierName }}</h6>
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>Shipping Date</th>
                                    <th>Order #</th>
                                    <th>Customer</th>
                                    <th>Items</th>
                                    <th>Pick Location</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="order in courierGroup.orders" :key="order.id">
                                    <td>{{ order.shippingDate }}</td>
                                    <td>{{ order.orderNumber }}</td>
                                    <td>{{ order.customerName }}</td>
                                    <td>{{ order.totalItems }} items</td>
                                    <td>{{ order.pickLocation }}</td>
                                    <td>
                                        <span :class="getStatusBadgeClass(order.status)">
                                            {{ order.status.replace('_', ' ') }}
                                        </span>
                                    </td>
                                    <td>
                                        <button class="btn btn-info btn-sm" @click="viewOrderDetails(order)">
                                            <i class="fa fa-eye"></i> Details
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <!-- Pagination and Row Size Controls -->
                    <div class="d-flex justify-content-between align-items-center mt-3">
                        <div>
                            <label for="itemsPerPage" class="form-label me-2">Rows per page:</label>
                            <select id="itemsPerPage" v-model="itemsPerPage" class="form-select form-select-sm" style="width: auto;">
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="20">20</option>
                            </select>
                        </div>
                        <nav aria-label="Page navigation">
                            <ul class="pagination justify-content-center mb-0">
                                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                    <button class="page-link" @click="changePage(currentPage - 1)">Previous</button>
                                </li>
                                <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: currentPage === page }">
                                    <button class="page-link" @click="changePage(page)">{{ page }}</button>
                                </li>
                                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                    <button class="page-link" @click="changePage(currentPage + 1)">Next</button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <!-- Order Details Modal -->
        <div class="modal fade" id="orderDetailsModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Order Details</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" v-if="selectedOrder">
                        <p><strong>Order Number:</strong> {{ selectedOrder.orderNumber }}</p>
                        <p><strong>Customer:</strong> {{ selectedOrder.customerName }}</p>
                        <p><strong>Pick Location:</strong> {{ selectedOrder.pickLocation }}</p>
                        <p><strong>Status:</strong> {{ selectedOrder.status.replace('_', ' ') }}</p>
                        <h6>Items to Pick:</h6>
                        <ul>
                            <li v-for="product in selectedOrder.items" :key="product.sku">
                                {{ product.name }} (SKU: {{ product.sku }}) - Quantity: {{ product.quantity }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Order Modal -->
        <div class="modal fade" id="addOrderModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Add Orders to Courier</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="addOrdersToCourier">
                            <div class="mb-2">
                                <label for="courierId" class="form-label col-form-label">Courier</label>
                                <select id="courierId" v-model="selectedCourierId" class="form-select" required>
                                    <option disabled value="">Select Courier</option>
                                    <option v-for="courier in availableCouriers" :key="courier.id" :value="courier.id">
                                        {{ courier.name }}
                                    </option>
                                </select>
                            </div>
                            <!-- Conditional section for 'Lain Lain' courier -->
                            <div v-if="selectedCourierId === 'other'" class="mb-2">
                                <label for="personnelSelect" class="form-label col-form-label">Select Personnel</label>
                                <select id="personnelSelect" v-model="selectedPersonnelId" class="form-select" required>
                                    <option disabled value="">Select Personnel</option>
                                    <option v-for="person in availablePersonnel" :key="person.id" :value="person.id">
                                        {{ person.name }} ({{ person.phone || 'No Phone' }})
                                    </option>
                                </select>
                            </div>
                            <div class="mb-2">
                                <label class="form-label col-form-label">Filter Orders</label>
                                <input type="text" v-model="orderFilter" class="form-control" placeholder="Search by Order Number or Customer Name">
                            </div>
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Select</th>
                                            <th>Order #</th>
                                            <th>Customer</th>
                                            <th>Items</th>
                                            <th>Pick Location</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="order in filteredOrders" :key="order.id">
                                            <td>
                                                <input type="checkbox" :value="order.id" v-model="selectedOrderIds">
                                            </td>
                                            <td>{{ order.orderNumber }}</td>
                                            <td>{{ order.customerName }}</td>
                                            <td>{{ order.totalItems }} items</td>
                                            <td>{{ order.pickLocation }}</td>
                                            <td>
                                                <span :class="getStatusBadgeClass(order.status)">
                                                    {{ order.status.replace('_', ' ') }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <button type="submit" class="btn btn-primary mt-3">Add Selected Orders</button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Modal } from 'bootstrap';

// {{ Define interfaces for data structures }}
interface OrderItem {
    sku: string;
    name: string;
    quantity: number;
}

interface Order {
    id: number;
    orderNumber: string;
    customerName: string;
    totalItems: number;
    pickLocation: string;
    status: 'PENDING' | 'PICKING' | 'PICKED' | 'READY_FOR_DELIVERY' | 'DELIVERED' | 'CANCELLED'; // Use literal types for status
    shippingDate: string; // Consider using Date type if appropriate
    courierId: string;
    items: OrderItem[];
    personnelId?: string; // Optional personnel ID
}

interface Courier {
    id: string;
    name: string;
}

interface Personnel {
    id: string;
    name: string;
    phone?: string | null; // Phone can be string or null
}

interface CourierGroup {
    courierId: string;
    courierName: string;
    orders: Order[];
}


const loading = ref(false);
// {{ Apply types to refs }}
const orders = ref<Order[]>([]);
const groupedOrders = ref<CourierGroup[]>([]);
const selectedOrder = ref<Order | null>(null);
const selectedCourierId = ref<string>(''); // Keep as string, could be specific courier ID or 'other'
const selectedOrderIds = ref<number[]>([]); // Array of order IDs (numbers)
const selectedPersonnelId = ref<string>('');
const orderFilter = ref('');
const availableOrders = ref<Order[]>([]);
const availableCouriers = ref<Courier[]>([
    { id: 'jne_reg', name: 'JNE Regular' },
    { id: 'sicepat_best', name: 'SiCepat BEST' },
    { id: 'jnt_ez', name: 'J&T Express EZ' },
    { id: 'grab_instant', name: 'Grab Instant' },
    { id: 'gojek_sameday', name: 'Gojek SameDay' },
    { id: 'other', name: 'Lain Lain' } // Special case
]);
const availablePersonnel = ref<Personnel[]>([
    { id: 'p001', name: 'Budi Santoso', phone: '081234567890' },
    { id: 'p002', name: 'Citra Lestari', phone: '081198765432' },
    { id: 'p003', name: 'Agus Wijaya', phone: null },
]);

let orderDetailsModalInstance: any | null = null;
let addOrderModalInstance: any | null = null;

const fetchOrders = async () => {
    loading.value = true;
    try {
        await new Promise(resolve => setTimeout(resolve, 500));
        // {{ Ensure mock data conforms to the Order interface }}
        const mockData: Order[] = [
            { id: 1, orderNumber: 'SO-00101', customerName: 'Alice Brown', totalItems: 3, pickLocation: 'Warehouse A-1', status: 'PENDING', shippingDate:'20-20-2025', courierId: 'jne_reg', items: [{ sku: 'P001', name: 'Product A', quantity: 2 }, { sku: 'P002', name: 'Product B', quantity: 1 }] },
            { id: 2, orderNumber: 'SO-00102', customerName: 'Bob White', totalItems: 1, pickLocation: 'Warehouse B-3', status: 'PICKING', shippingDate:'20-20-2025', courierId: 'sicepat_best', items: [{ sku: 'P003', name: 'Product C', quantity: 1 }] },
            { id: 3, orderNumber: 'SO-00103', customerName: 'Charlie Green', totalItems: 5, pickLocation: 'Warehouse A-2', status: 'PICKED', shippingDate:'20-20-2025', courierId: 'jne_reg', items: [{ sku: 'P001', name: 'Product A', quantity: 3 }, { sku: 'P004', name: 'Product D', quantity: 2 }] },
            { id: 4, orderNumber: 'SO-00104', customerName: 'David Black', totalItems: 2, pickLocation: 'Warehouse C-1', status: 'PENDING', shippingDate:'20-20-2025', courierId: 'grab_instant', items: [{ sku: 'P005', name: 'Product E', quantity: 2 }] },
            { id: 5, orderNumber: 'SO-00105', customerName: 'Eve Davis', totalItems: 4, pickLocation: 'Warehouse B-1', status: 'READY_FOR_DELIVERY', shippingDate:'20-20-2025', courierId: 'sicepat_best', items: [{ sku: 'P002', name: 'Product B', quantity: 4 }] },
        ];
        orders.value = mockData;
        availableOrders.value = mockData;
        groupOrdersByCourier();
    } catch (error) {
        console.error("Error fetching orders:", error);
    } finally {
        loading.value = false;
    }
};

const groupOrdersByCourier = () => {
    // {{ Use a Record type for the map for better type safety }}
    const courierMap: Record<string, CourierGroup> = {};
    orders.value.forEach(order => {
        if (!courierMap[order.courierId]) {
            courierMap[order.courierId] = {
                courierId: order.courierId,
                courierName: getCourierName(order.courierId),
                orders: [] // Initialize with empty Order array
            };
        }
        courierMap[order.courierId].orders.push(order);
    });
    groupedOrders.value = Object.values(courierMap);
};

// {{ Add type annotation for courierId parameter }}
const getCourierName = (courierId: string): string => {
    // Find courier in the availableCouriers ref for better maintainability
    const courier = availableCouriers.value.find(c => c.id === courierId);
    return courier ? courier.name : 'Unknown Courier';
};

// {{ Add type annotation for status parameter and return type }}
const getStatusBadgeClass = (status: Order['status']): string => {
    const classes: Record<Order['status'], string> = { // Use Record with Order status type
        PENDING: 'badge bg-secondary',
        PICKING: 'badge bg-warning text-dark',
        PICKED: 'badge bg-info text-dark',
        READY_FOR_DELIVERY: 'badge bg-success',
        DELIVERED: 'badge bg-primary',
        CANCELLED: 'badge bg-danger'
    };
    return classes[status] || 'badge bg-light text-dark';
};

// {{ Add type annotation for order parameter }}
const viewOrderDetails = (order: Order) => {
    selectedOrder.value = order;
    if (orderDetailsModalInstance) {
        orderDetailsModalInstance.show();
    }
};

const showAddOrderModal = () => {
    selectedCourierId.value = '';
    selectedOrderIds.value = [];
    selectedPersonnelId.value = ''; // Reset personnel ID as well
    orderFilter.value = ''; // Reset filter
    if (addOrderModalInstance) {
        addOrderModalInstance.show();
    }
};

const addOrdersToCourier = () => {
    // No type changes needed here as selectedOrderIds is already number[]
    // and availableOrders is Order[]
    const selectedOrders = availableOrders.value.filter(order => selectedOrderIds.value.includes(order.id));
    selectedOrders.forEach(order => {
        order.courierId = selectedCourierId.value;
        // Clear personnelId if courier is not 'other'
        order.personnelId = selectedCourierId.value === 'other' ? selectedPersonnelId.value : undefined;
    });
    groupOrdersByCourier(); // Re-group based on updated orders
    // Consider updating availableOrders if assigned orders should be removed from the list
    // availableOrders.value = availableOrders.value.filter(order => !selectedOrderIds.value.includes(order.id));
    if (addOrderModalInstance) {
        addOrderModalInstance.hide();
    }
};

// {{ Apply Order type to computed property }}
const filteredOrders = computed<Order[]>(() => {
    if (!orderFilter.value) {
        // Optimization: return all if filter is empty
        return availableOrders.value;
    }
    const filterLower = orderFilter.value.toLowerCase();
    return availableOrders.value.filter(order => {
        return (
            order.orderNumber.toLowerCase().includes(filterLower) ||
            order.customerName.toLowerCase().includes(filterLower)
        );
    });
});

onMounted(() => {
    fetchOrders();

    const orderDetailsModalElement = document.getElementById('orderDetailsModal');
    if (orderDetailsModalElement) {
        orderDetailsModalInstance = new Modal(orderDetailsModalElement);
    }

    const addOrderModalElement = document.getElementById('addOrderModal');
    if (addOrderModalElement) {
        addOrderModalInstance = new Modal(addOrderModalElement);
    }
});

const currentPage = ref(1);
// {{ Ensure itemsPerPage is treated as a number }}
const itemsPerPage = ref(10); // Keep as number

const shippingDateFilter = ref('');
const orderNumberFilter = ref('');
const customerFilter = ref('');

// {{ Apply CourierGroup type to computed property }}
const filteredGroupedOrders = computed<CourierGroup[]>(() => {
    // Optimization: return original if no filters are active
    if (!shippingDateFilter.value && !orderNumberFilter.value && !customerFilter.value) {
        return groupedOrders.value;
    }

    const orderNumLower = orderNumberFilter.value.toLowerCase();
    const customerLower = customerFilter.value.toLowerCase();

    return groupedOrders.value.map(courierGroup => {
        const filtered = courierGroup.orders.filter(order => {
            // Ensure date comparison logic is robust if needed (e.g., using Date objects)
            const matchesShippingDate = shippingDateFilter.value ? order.shippingDate.includes(shippingDateFilter.value) : true;
            const matchesOrderNumber = orderNumberFilter.value ? order.orderNumber.toLowerCase().includes(orderNumLower) : true;
            const matchesCustomer = customerFilter.value ? order.customerName.toLowerCase().includes(customerLower) : true;
            return matchesShippingDate && matchesOrderNumber && matchesCustomer;
        });
        // Return a new object to maintain reactivity if needed, or modify in place if safe
        return { ...courierGroup, orders: filtered };
    }).filter(group => group.orders.length > 0); // Filter out groups with no matching orders
});

// {{ Apply CourierGroup type to computed property }}
const paginatedGroupedOrders = computed<CourierGroup[]>(() => {
    const numItemsPerPage = Number(itemsPerPage.value); // Ensure it's a number
    const start = (currentPage.value - 1) * numItemsPerPage;
    const end = start + numItemsPerPage;

    // Paginate the orders *within* each filtered group
    return filteredGroupedOrders.value.map(courierGroup => {
        const paginated = courierGroup.orders.slice(start, end);
        return { ...courierGroup, orders: paginated };
    });
    // Note: This pagination logic applies per group. If you want pagination across *all* filtered orders
    // regardless of group, the logic needs significant change.
});


// {{ Apply number type to computed property }}
const totalPages = computed<number>(() => {
    // Calculate total based on *filtered* orders if pagination should reflect filters
    const totalFilteredOrders = filteredGroupedOrders.value.reduce((sum, group) => sum + group.orders.length, 0);
    const numItemsPerPage = Number(itemsPerPage.value); // Ensure it's a number
    if (numItemsPerPage <= 0) return 1; // Avoid division by zero or negative
    return Math.ceil(totalFilteredOrders / numItemsPerPage);
});


const changePage = (page: number) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};


watch(itemsPerPage, () => {
    if (currentPage.value > totalPages.value) {
        currentPage.value = Math.max(1, totalPages.value); // Reset to last valid page or 1
    }
});

// {{ Watch filters to reset currentPage to 1 when filters change }}
watch([shippingDateFilter, orderNumberFilter, customerFilter], () => {
    currentPage.value = 1;
});


</script>

<style scoped>
.badge {
    padding: 0.5em 0.75em;
    font-size: 0.8em;
    text-transform: capitalize;
}
.table th {
    cursor: default;
}
.form-select-sm, .form-control-sm {
    font-size: 0.875rem;
}
</style>
